import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { TabsInnerWrapComponent } from '../../components/tabs-inner-wrap/tabs-inner-wrap.component';
import { DetailsSectionComponent } from '../../components/details-container/details-section.component';
import {
  KebabMenuItem,
  QaiButtonComponent,
  QaiLoadingComponent,
  QaiMultiSelectComponent,
  QaiMultiSelectOption,
  QaiTableComponent,
  SortDirection,
  TableRow,
} from '@qai/ui';
import { ActivatedRoute } from '@angular/router';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { IAssignments } from 'src/app/interfaces/self-serve/responses/iassignments';
import { AuthorReactiveFormService } from '../../services/author-reactive-form.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { ICurrentUser } from 'src/app/interfaces/icurrent-user';
import {
  catchError,
  firstValueFrom,
  forkJoin,
  Observable,
  of,
  tap,
} from 'rxjs';
import { ICreatePersona } from 'src/app/interfaces/self-serve/responses/icreate-persona';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { formatLongMonthDate } from 'src/app/utils/date-parser.util';
import { IPersonaInfo } from 'src/app/interfaces/self-serve/assignment-manager/ipersona-info';

export const TESTING_TABLE_HEADERS = [
  'Name',
  'Number of attempts',
  'Date of last attempt',
];

@Component({
  selector: 'tab-testing-details',
  standalone: true,
  templateUrl: './testing-details.component.html',
  styleUrls: ['./testing-details.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    DetailsSectionComponent,
    TabsInnerWrapComponent,
    QaiTableComponent,
    QaiButtonComponent,
    QaiMultiSelectComponent,
    QaiLoadingComponent,
  ],
})
export class TestingDetailsComponent implements OnInit {
  @ViewChild('multiselect') searchInput!: QaiMultiSelectComponent;

  tableData: TableRow[] = [];
  readonly headers = TESTING_TABLE_HEADERS;
  initialSortColumn: string = TESTING_TABLE_HEADERS[0];
  initialSortDirection: SortDirection = 'asc';
  public searchOptions: QaiMultiSelectOption[] = [];
  clearOption = { enable: true, label: 'Clear users' };
  visibleOptionsSelectionMode = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };

  isLoading = false;
  sendAssignmentNetworkCallInprogress = false;

  accountUuid: string | null = null;

  form!: FormGroup;
  baseForm!: FormGroup;
  overallForm!: FormGroup;
  callUuid: string = '';
  readOnly: boolean = false;
  readyForPublish: boolean = false;
  description: string =
    'Search for users who will be testing the simulation, then send them the test assignment.';

  testersList: string[] = [];
  currentUser: ICurrentUser | undefined;
  defaultSelection: string[] = [];
  assignments: IAssignments[] = [];
  networkRequestInProgress: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private cdRef: ChangeDetectorRef,
    private authorReactiveFormService: AuthorReactiveFormService
  ) { }

  async ngOnInit() {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);

    await this.loadForm();

    this.currentUser = SelfServeLocalStorageUtil.getCurrentUser();

    this.isLoading = true;
    forkJoin([this.getUsers(), this.getAssignments()]).subscribe(
      ([users, assignments]) => {
        this.isLoading = false;
        this.addCurrentUserAsDefault(assignments);
        this.cdRef.detectChanges();
      }
    );
  }

  private async loadForm(): Promise<void> {
    this.baseForm = await firstValueFrom(
      this.authorReactiveFormService.getForm(this.callUuid)
    );

    this.form = this.baseForm.get(
      SimulationConstants.CALL_PERSONA_PATH
    ) as FormGroup;

    this.baseForm.statusChanges?.subscribe(() => {
      this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
      this.readyForPublish = this.baseForm.valid;
    });
  }

  getUsers(): Observable<IAssignments[]> {
    const selectedAccountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const currentAccountUuid = localStorage.getItem('current_account_uuid');
    const accountUuid = selectedAccountUuid || currentAccountUuid || '';
    return this.authorService.getUsersForTesting(accountUuid).pipe(
      tap((res) => {
        this.searchOptions = this.transformSearchDataToOptions(res);
      }),
      catchError((err) => {
        console.error(err);
        return of([]);
      })
    );
  }

  getAssignments(): Observable<IAssignments[]> {
    const moduleUuid = this.baseForm?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '';
    return this.authorService.getAttempts(this.callUuid, moduleUuid).pipe(
      tap((res: IAssignments[]) => {
        this.assignments = res;
        this.tableData = this.transformTestingDataToTableRows(res);
      }),
      catchError((err) => {
        console.error(err);
        return of([]);
      })
    );
  }

  addCurrentUserAsDefault(users: IAssignments[]): void {
    const userUuid = this.currentUser?.userUuid;
    if (!userUuid) return;

    const isAlreadyAssigned = users.some((user) => user.userUuid === userUuid);
    const isAlreadySelected = this.testersList.includes(userUuid);

    if (isAlreadyAssigned || isAlreadySelected) return;

    const dropdownOption = this.searchOptions.find(
      (opt) => opt.value === userUuid
    );

    if (dropdownOption) {
      this.defaultSelection = [dropdownOption.value];
      this.testersList.push(dropdownOption.value);
    }
  }

  filterOutExistingAssignments() {
    const existingUuids = new Set(
      this.assignments.map((item) => item.userUuid)
    );

    this.testersList = this.testersList.filter(
      (uuid) => !existingUuids.has(uuid)
    );
  }

  handleSendAssignmentClick(event: Event) {
    console.log('Send Test Assignment button clicked.!', event);
    this.networkRequestInProgress = true;
    this.sendTestAssignments();
  }

  sendTestAssignments() {
    this.filterOutExistingAssignments();
    if (this.testersList.length === 0) {
      this.defaultSelection = [];
      return;
    }
    const moduleUuid = this.baseForm?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '';
    this.authorService
      .sendTestAssignment(this.callUuid, this.testersList, moduleUuid)
      .subscribe({
        next: () => {
          this.defaultSelection = [];
          this.testersList = [];
          this.searchInput.clearSelections();
          this.getAssignments().subscribe();
          this.networkRequestInProgress = false;
        },
        error: (err) => {
          console.error(err);
          this.networkRequestInProgress = false;
        },
      });
  }

  transformSearchDataToOptions(apiData: any[]): QaiMultiSelectOption[] {
    return apiData.map((item) => ({
      value: item.uuid,
      label: `${item.firstName} ${item.lastName}`,
      description: item.email,
    }));
  }

  valueChange(event: string) {
    console.log('value changed');
  }

  selectionChange(event: string) {
    console.log('selection changed');
  }

  searchValueChange(event: string[]) {
    console.log('search value changed:', event);
    this.testersList = event;
  }

  searchSelectionChange(event: QaiMultiSelectOption[]) {
    console.log('search selection changed:', event);
  }

  createTestingTableRow(
    id: string | number,
    name: string,
    numberOfAttempts: number,
    dateOfLastAttempt: string,
    options: KebabMenuItem[]
  ): TableRow {
    const row = {
      id,
      [TESTING_TABLE_HEADERS[0]]: { type: 'text', value: name },
      [TESTING_TABLE_HEADERS[1]]: { type: 'text', value: numberOfAttempts },
      [TESTING_TABLE_HEADERS[2]]: { type: 'text', value: dateOfLastAttempt },
    } as const;
    return { ...row, options } as unknown as TableRow;
  }

  onTableRowAction(event: {
    action: KebabMenuItem;
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    if (!event.action.text) return;

    switch (event.action.text.toLowerCase()) {
      case 'remove assignee':
        this.removeAssignment(event.rowData.id as string);
        break;

      default:
        break;
    }
  }

  removeAssignment(userUuid: string): void {
    const moduleUuid = this.baseForm?.get(SimulationConstants.MODULE_UUID_PATH)?.value || '';
    this.authorService.removeAssignment(this.callUuid, userUuid, moduleUuid).subscribe({
      next: () => {
        console.log(`Assignment removed for user: ${userUuid}`);
        this.getUsers();
        this.getAssignments().subscribe();
      },
      error: (err: any) => {
        console.error('Failed to remove assignee', err);
      },
    });
  }

  transformTestingDataToTableRows(apiData: IAssignments[]): TableRow[] {
    return apiData.map((item) =>
      this.createTestingTableRow(
        item.userUuid,
        item.userName,
        item.numberOfAttempts,
        formatLongMonthDate(item.lastAttemptDate),
        [
          {
            type: 'click',
            text: 'Remove assignee',
          },
        ]
      )
    );
  }
}
